import { StorageAdapter } from '../storage/StorageAdapter'
import { toText } from '../storage/json-codec'
import { newId } from './ids'
import { dbg } from '../logger'
import { getOfflineUserId } from '../session'

/**
 * BaseRepository — el nucleo cero-perdida (CAP-01) de offline-v2.
 *
 * REGLA DE CAPAS (STOR-05): SOLO los repositorios emiten SQL, y lo hacen siempre a
 * traves del StorageAdapter; NADIE fuera de storage/ conoce el motor SQLite
 * (@capacitor-community/sqlite en nativo / wa-sqlite|OPFS en web). Este modulo solo emite SQL via
 * ../storage/StorageAdapter; nada del motor directamente.
 *
 * FRONTERA F2/F3: el body del outbox que se escribe aqui es NORMALIZADO y minimo
 * (ids, refs, valores de campo). NO se construyen payloads REST, NO se resuelve
 * client_uuid->server_id, NO se manejan blobs de fotos. Eso es F3/F5 al drenar.
 *
 * GARANTIA ATOMICA: cada mutacion Tier A escribe su fila de datos Y su fila de outbox
 * en UNA sola operacion SQLite — nunca una sin la otra. Hay dos caminos limpios:
 *   - NO-coalesce (default + TODOS los event ops): `executeSet([data, outbox], true)`,
 *     el batch atomico de 2 statements que el docstring del StorageAdapter nombra como
 *     el camino cero-perdida.
 *   - COALESCE (solo latest-state): `transaction(fn)` -> `_saveOptimisticTx(handle, ...)`,
 *     porque coalescer LEE-luego-escribe (UPDATE de la fila pending; si 0 filas, INSERT).
 * NUNCA escribir data y outbox en dos `run()` top-level separados (esa es justo la
 * ventana de crash que esta fase cierra). NUNCA anidar executeSet/transaction dentro
 * de una transaccion ya abierta — alli se usa _saveOptimisticTx con run(...,false).
 *
 * COSTURA DE EJECUTOR INYECTABLE: `saveOptimistic(spec, { db = StorageAdapter } = {})`.
 * `db` es un handle inyectable que por DEFECTO es el StorageAdapter real, asi que en
 * produccion los llamadores omiten el 2o argumento y NO hay cambio de comportamiento
 * (costura pura). El gate de crash de 03-02 inyecta un `db` respaldado por sql.js que
 * expone executeSet(set, transaction) y transaction(fn) con la misma semantica, para
 * ejercer el CODIGO REAL contra sql.js (no solo SQL crudo).
 */
export class BaseRepository {
  /**
   * @param {string} tableName  nombre de tabla (occurrences, answers, ...).
   * @param {string} entity     valor de outbox.entity (occurrence | task).
   * @param {string} pk         columna conflict-target por defecto: 'client_uuid'
   *   para occurrences/answers/addons/signatures/tasks; 'uuid' para task_evidence.
   */
  constructor (tableName, entity, pk = 'client_uuid') {
    this.tableName = tableName
    this.entity = entity
    this.pk = pk
  }

  /** Lee una fila por su PK. Devuelve la fila cruda (el caller parsea JSON si aplica). */
  async get (id) {
    const result = await StorageAdapter.query(
      `SELECT * FROM ${this.tableName} WHERE ${this.pk}=?`, [id]
    )
    return (result && result.values && result.values[0]) || null
  }

  /** Lista filas con un WHERE opcional. Devuelve el array de filas. */
  async list (whereSql = '', params = []) {
    const result = await StorageAdapter.query(
      `SELECT * FROM ${this.tableName} ${whereSql}`.trim(), params
    )
    return (result && result.values) || []
  }

  /**
   * Construye el UPSERT de la fila de datos. Genera la lista de columnas a partir de
   * las CLAVES presentes en dataRow (se mantiene generico para millones de filas).
   * `conflictTarget` = la(s) columna(s) del ON CONFLICT (default this.pk; Answers pasa
   * 'occurrence_id, item_id'). El DO UPDATE setea cada columna NO-conflicto = excluded.
   * @returns {{ statement: string, values: any[] }}
   */
  _buildUpsert (dataRow, { conflictTarget } = {}) {
    const target = conflictTarget ?? this.pk
    const cols = Object.keys(dataRow)
    // SEGURIDAD (PR review #4): los nombres de columna se interpolan crudos en el SQL (los VALORES
    // sí van parametrizados). Validar cada columna contra un identificador SQL seguro convierte una
    // clave-no-columna accidental (p.ej. una clave del payload REST como `tag`/`role`/`users` que no
    // existe en la tabla, o input dinámico) en un error EXPLÍCITO en vez de SQL roto/inyectable.
    for (const c of cols) {
      if (!/^[a-z_][a-z0-9_]*$/.test(c)) {
        throw new Error(`BaseRepository: nombre de columna inválido '${c}' en tabla ${this.tableName}`)
      }
    }
    const placeholders = cols.map(() => '?').join(', ')
    // BIND-SAFETY (CAP-bind-safety): cada valor pasa por toText() del json-codec compartido, asi
    // un objeto/array crudo (form-reading {last,current,difference}, checkbox/multinumeric [],
    // extra_properties []) se serializa a JSON TEXT en VEZ de reventar el bind del plugin (que
    // dejaria la fila + su outbox sin escribir y la foto huerfana). Escalares pasan tal cual.
    const values = cols.map(c => toText(dataRow[c]))
    // columnas del conflict-target (split por coma) NO se actualizan.
    const targetCols = target.split(',').map(s => s.trim())
    const updates = cols
      .filter(c => !targetCols.includes(c))
      .map(c => `${c}=excluded.${c}`)
    // si todo es conflict-target (raro) -> DO NOTHING para no romper el SQL.
    const conflictClause = updates.length
      ? `ON CONFLICT(${target}) DO UPDATE SET ${updates.join(', ')}`
      : `ON CONFLICT(${target}) DO NOTHING`
    const statement =
      `INSERT INTO ${this.tableName} (${cols.join(', ')}) VALUES (${placeholders}) ${conflictClause}`
    return { statement, values }
  }

  /**
   * Construye el INSERT de la fila de outbox. status arranca 'pending' (vocabulario
   * locked: pending->in_flight->done|dead); body se JSON.stringify (columna TEXT);
   * mutation_uuid = un id NUEVO por fila; next_attempt_at/created_at = epoch ms.
   * Tambien expone `coalesceBody` (el JSON ya serializado) para el camino coalesce.
   * @returns {{ statement: string, values: any[], coalesceBody: string }}
   */
  _buildOutboxInsert ({ clientUuid, entity, op, body }) {
    const serialized = JSON.stringify(body ?? {})
    // user_id = dueño de la op (usuario autenticado al capturar). El drain filtra por él para que un
    // device compartido no pushee lo de otro usuario. null si no se conoce -> drena bajo cualquiera.
    return {
      statement:
        'INSERT INTO outbox (mutation_uuid, client_uuid, entity, op, body, status, retries, next_attempt_at, last_error, created_at, user_id) ' +
        'VALUES (?,?,?,?,?,?,?,?,?,?,?)',
      values: [newId(), clientUuid, entity, op, serialized, 'pending', 0, Date.now(), null, Date.now(), getOfflineUserId()],
      coalesceBody: serialized
    }
  }

  /**
   * Nucleo de atomicidad COALESCE-aware que recibe un handle de tx YA ABIERTO y corre
   * CADA statement via db.run(sql, values, false) (transaction=false) — NUNCA abre su
   * propio BEGIN; el caller es el dueno de la transaccion. Reutilizable por
   * AnswersRepository dentro de su propia transaccion (evita doble-BEGIN, Open Q3).
   *
   * Logica de coalesce (solo se usa por el camino coalesce de saveOptimistic y por
   * AnswersRepository):
   *   1. UPDATE outbox SET ... WHERE client_uuid=? AND op=? AND status='pending'
   *      (coalesce en sitio de la fila pending; jamas toca in_flight/done/dead). Refresca
   *      body/mutation_uuid/created_at/next_attempt_at (dato fresco -> intenta drenar pronto);
   *      retries y last_error se dejan INTACTOS (ver comentario en el cuerpo, PR review #7).
   *   2. el UPSERT de la fila de datos.
   *   3. si el UPDATE afecto 0 filas (no habia pending) -> INSERT de una nueva fila pending.
   * @returns {Promise<string>} el uuid de la entidad.
   */
  async _saveOptimisticTx (db, { uuid, dataStmt, outboxInsert, op }) {
    // 1) coalesce la fila pending en sitio (refresca body + mutation_uuid + created_at).
    //    next_attempt_at se resetea a "ahora" [review Hugo r3 #7]: sin esto una edicion nueva del
    //    usuario heredaba el backoff exponencial de un intento previo fallido y el dato fresco esperaba
    //    el next_attempt_at VIEJO. `retries` se deja INTACTO A PROPOSITO: resetearlo dejaria que el solo
    //    acto de re-editar un campo re-armara el contador de dead-letter, asi que un op estructuralmente
    //    roto (payload que el backend siempre rechaza) reintentaria para siempre. `last_error` tambien
    //    queda: es diagnostico ligado a ese mismo `retries`, y el proximo intento lo refresca solo.
    const now = Date.now()
    const upd = await db.run(
      "UPDATE outbox SET body=?, mutation_uuid=?, created_at=?, next_attempt_at=? WHERE client_uuid=? AND op=? AND status='pending'",
      [outboxInsert.coalesceBody, newId(), now, now, uuid, op],
      false
    )
    // 2) la fila de datos (UPSERT).
    await db.run(dataStmt.statement, dataStmt.values, false)
    // 3) si no habia fila pending que coalescer -> INSERT una nueva.
    //    el backend SQLite nativo (@capacitor-community/sqlite) devuelve { changes: { changes, lastId } }; se lee defensivo.
    const affected = upd && upd.changes && typeof upd.changes.changes === 'number'
      ? upd.changes.changes
      : (upd && typeof upd.changes === 'number' ? upd.changes : 0)
    if (affected === 0) {
      await db.run(outboxInsert.statement, outboxInsert.values, false)
    }
    return uuid
  }

  /**
   * Escritura optimista: fila de datos + fila de outbox, atomica (CAP-01).
   * @param {object} spec
   * @param {string} [spec.clientUuid]  id estable de la entidad (si se omite se deriva).
   * @param {object} spec.dataRow       la fila de datos a UPSERT.
   * @param {string} spec.op            outbox.op normalizado.
   * @param {string} [spec.entity]      outbox.entity (default this.entity).
   * @param {object} [spec.body]        body normalizado del outbox (default dataRow).
   * @param {boolean} [spec.coalesce]   true = latest-state (coalesce la fila pending).
   * @param {string} [spec.conflictTarget]  conflict-target del UPSERT (default this.pk).
   * @param {object} [exec]
   * @param {object} [exec.db]          ejecutor inyectable (default StorageAdapter real).
   * @returns {Promise<string>} el uuid de la entidad.
   */
  async saveOptimistic (
    { clientUuid, dataRow = {}, op, entity, body, coalesce = false, conflictTarget } = {},
    { db = StorageAdapter } = {}
  ) {
    const uuid = clientUuid ?? dataRow.client_uuid ?? dataRow.uuid ?? newId()

    // Rellena el PK en la fila de datos (client_uuid o uuid segun la tabla).
    const filledRow = { ...dataRow, [this.pk]: uuid }

    const dataStmt = this._buildUpsert(filledRow, { conflictTarget: conflictTarget ?? this.pk })
    const outboxInsert = this._buildOutboxInsert({
      clientUuid: uuid,
      entity: entity ?? this.entity,
      op,
      body: body ?? dataRow
    })

    if (coalesce) {
      // CAMINO COALESCE (latest-state): UNA transaccion, _saveOptimisticTx corre cada
      // statement con run(...,false) -> sin doble-BEGIN (Open Q3).
      await db.transaction(async (handle) =>
        this._saveOptimisticTx(handle, { uuid, dataStmt, outboxInsert, op })
      )
    } else {
      // CAMINO NO-COALESCE (default + todos los event ops): batch atomico both-or-neither.
      await db.executeSet([dataStmt, outboxInsert], true)
    }

    // Log de CADA item guardado en SQLite (cero-pérdida VISIBLE). Vía logger central -> funciona DENTRO
    // del APK cuando el debug está ON (localStorage offline_v2_debug=1), no solo en `quasar dev`.
    // PRIVACIDAD: se loggean SOLO las columnas presentes (keys), NUNCA los VALORES del dataRow — el
    // ring buffer es recuperable y podría filtrar PII (respuestas/firmas). El uuid ya va en el mensaje.
    dbg('save', `💾 ${this.tableName} op=${op} key=${uuid} (${coalesce ? 'coalesce-tx' : 'batch-atómico'})`, { cols: Object.keys(dataRow || {}) })

    return uuid
  }
}
